import {
  Button,
  EmojiArea,
  ImageInput,
  Loader,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarImageInputWrapper,
  SidebarInputsWrapper,
} from 'components';
import TextInput from 'components/Inputs/TextInput/TextInputV2';
import { Layout } from 'pages/PersonalChat/ChatUserList/styled';
import { Title } from 'pages/UserProfile/Content/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LeftCloseArrow } from 'static';
import { closeModal } from 'store/actionCreators/modal';
import { ChatUser } from 'store/reducers/chat';
import { Chat } from 'store/reducers/chat';
import {
  createChatGroupLoading,
  updateChatGroupLoading,
} from 'store/selectors/chat';
import { getAvailableActiveCommunities } from 'store/selectors/communities';
import {
  getCreateGroupLoading,
  getCurrentCommunity,
} from 'store/selectors/currentCommunity';
import { getData } from 'store/selectors/modal';

import CommunitySelect from './CommunitySelect';
import { FIELDS } from './form';
import { useCreateGroup } from './hooks';
import { BackBtnWrapper, TextInputBlock } from './styled';

const CreateGroup = ({ chat }: { chat?: boolean }) => {
  const { t } = useTranslation();
  const { communityId: communityIdFromParams } = useParams<{
    communityId: string;
  }>();
  const communities = useSelector(getAvailableActiveCommunities);
  const dispatch = useDispatch();
  const [nextStep, setNextStep] = useState<boolean>(false);
  const data = useSelector(getData) as {
    chat: Chat;
    members: ChatUser[];
    communityId: string;
    isChatUpdate: boolean;
  };
  const [selectedCommunityId, setSelectedCommunityId] = useState<string | null>(
    null,
  );
  const community = useSelector(getCurrentCommunity);
  const isLoading = useSelector(getCreateGroupLoading);
  const isUpdateLoading = useSelector(updateChatGroupLoading);
  const isLoadingChatGroup = useSelector(createChatGroupLoading);

  const {
    chat: chatData = null,
    members = [],
    isChatUpdate = false,
  } = data || {};

  const { onSubmit, register, errors, unregister, setValue, control } =
    useCreateGroup(
      selectedCommunityId || community?.id,
      !!chat,
      chatData,
      members,
      isChatUpdate,
      chatData?.admin,
    );

  const handleClose = () => {
    dispatch(closeModal());
  };

  return (
    <Sidebar>
      <form onSubmit={onSubmit}>
        <SidebarHeader
          label={
            isChatUpdate
              ? t('chat.modal.editRoom')
              : chat
              ? t('chat.create.title')
              : t('group.createGroup')
          }
          onCloseClick={handleClose}
        />
        <SidebarContent fullHeight>
          <SidebarInputsWrapper>
            {!communityIdFromParams && !isChatUpdate && !nextStep ? (
              <>
                <Title customPadding={'26px 0px'}>
                  <p>{'Select community'}</p>
                </Title>
                <CommunitySelect
                  value={selectedCommunityId}
                  nextStep={() => setNextStep(true)}
                  onChange={setSelectedCommunityId}
                  communities={communities}
                />
              </>
            ) : (
              <>
                {!communityIdFromParams && !isChatUpdate && nextStep && (
                  <BackBtnWrapper>
                    <Layout.Header.Close onClick={() => setNextStep(false)}>
                      <LeftCloseArrow width={24} height={24} />
                    </Layout.Header.Close>
                  </BackBtnWrapper>
                )}
                {chat && (
                  <SidebarImageInputWrapper>
                    <ImageInput
                      label={t('profile.selectImage')}
                      name={FIELDS.IMAGE}
                      register={register}
                      unregister={unregister}
                      setValue={setValue}
                      defaultValue={chatData?.logo || ''}
                      error={errors[FIELDS.IMAGE]}
                    />
                  </SidebarImageInputWrapper>
                )}
                <TextInputBlock>
                  <TextInput
                    register={register}
                    width="100%"
                    name={FIELDS.GROUP_NAME}
                    label={chat ? t('chat.create.name') : t('group.groupName')}
                    error={errors[FIELDS.GROUP_NAME]}
                  />
                </TextInputBlock>
                {chat && (
                  <EmojiArea
                    rows={6}
                    width="100%"
                    label={
                      chat ? t('chat.create.description') : t('profile.about')
                    }
                    name={FIELDS.ABOUT}
                    register={register}
                    error={errors[FIELDS.ABOUT]}
                    control={control}
                  />
                )}
              </>
            )}
          </SidebarInputsWrapper>
        </SidebarContent>
        <SidebarFooter>
          <Button
            type="submit"
            variant={
              isLoading || isLoadingChatGroup || isUpdateLoading
                ? 'secondary'
                : null
            }
            disabled={isLoading || isLoadingChatGroup || isUpdateLoading}
          >
            {isLoading || isLoadingChatGroup || isUpdateLoading ? (
              <Loader type="button" size="28px" thickness={2} />
            ) : isChatUpdate ? (
              t('chat.update.btn')
            ) : chat ? (
              t('chat.create.btnNext')
            ) : (
              t('common.create')
            )}
          </Button>
        </SidebarFooter>
      </form>
    </Sidebar>
  );
};

export default CreateGroup;
